import { useEffect } from 'react'
import {
    FiTool, FiSettings, FiActivity, FiDroplet,
    FiBattery, FiSliders, FiWind, FiMonitor, FiToggleRight,
    FiFilter, FiLink, FiGitMerge, FiCalendar, FiClock, FiCheckCircle, FiImage,
    FiHash, FiZap, FiFileText
} from 'react-icons/fi'
import { FaFilter } from 'react-icons/fa'
import { ReactComponent as GeneratorIcon } from '../../shared/img/icon/generator.svg'
import PhotoGallery from '../../shared/components/PhotoGallery/PhotoGallery'
import './GeneratorReportModal.css'

const Field = ({ icon: Icon, label, value, iconSize = 11 }) => (
    <div className="grm-field">
        <span className="grm-field-label">
            {Icon && <Icon size={iconSize} />}
            {label}
        </span>
        <span className="grm-field-value">{value || '—'}</span>
    </div>
)

const MaintRow = ({ label, last, next }) => (
    <div className="grm-maint-row">
        <span className="grm-maint-name">{label}</span>
        <span className="grm-maint-date">{last || '—'}</span>
        <span className="grm-maint-date grm-maint-date-next">{next || '—'}</span>
    </div>
)

function GeneratorReportModal({ generator, onClose }) {

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose() }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [onClose])

    if (!generator) return null

    return (
        <div className="grm-overlay" onClick={onClose}>
            <div className="grm-box" onClick={(e) => e.stopPropagation()}>

                <button className="grm-close shd-modal-close" onClick={onClose}>X</button>

                {/* ── Header ── */}
                <div className="grm-header">
                    <div className="grm-header-top">
                        <div className="grm-header-icon"><GeneratorIcon className="grm-generator-svg" /></div>
                        <div>
                            <p className="grm-name">{generator.name}</p>
                            <span className="grm-accent" />
                        </div>
                    </div>
                    <div className="grm-header-bottom">
                        <span className={generator.disabled ? 'grm-badge-disabled' : 'grm-badge-active'}>
                            {generator.disabled ? 'Desabilitado' : 'Ativo'}
                        </span>
                    </div>
                </div>

                {/* ── Body ── */}
                <div className="grm-body">

                    <div className="grm-section">
                        <div className="grm-section-header">
                            <FiSettings size={14} />
                            <span>Especificações Técnicas</span>
                        </div>
                        <div className="grm-grid">
                            <Field icon={FiHash}        label="Número de Série"      value={generator.serialNumber} />
                            <Field icon={FiHash}        label="Nº de Patrimônio"     value={generator.patrimonyNumber} />
                            <Field icon={FiTool}        label="Fabricante"        value={generator.manufacturer} />
                            <Field icon={FiSettings}    label="Motor"             value={generator.engine} />
                            <Field icon={FiActivity}    label="Potência"          value={generator.power} />
                            <Field icon={FiZap}         label="Alternador"        value={generator.alternator} />
                            <Field icon={FiDroplet}     label="Cap. do Tanque"    value={generator.tankCapacity} />
                            <Field icon={FiBattery}     label="Bateria"           value={generator.battery} />
                            <Field icon={FiToggleRight} label="QTA"               value={generator.qta} />
                            <Field icon={FiMonitor}     label="Painel"            value={generator.panel} />
                            <Field icon={FiSliders}     label="Sist. de Injeção"  value={generator.injectionSystem} />
                            <Field icon={FiWind}        label="Arrefecimento"     value={generator.coolingSystem} />
                        </div>
                    </div>

                    {generator.description && (
                        <div className="grm-section">
                            <div className="grm-section-header">
                                <FiFileText size={14} />
                                <span>Descrição</span>
                            </div>
                            <p className="grm-description">{generator.description}</p>
                        </div>
                    )}

                    <div className="grm-section">
                        <div className="grm-section-header">
                            <FiFilter size={14} />
                            <span>Filtros e Consumíveis</span>
                        </div>
                        <div className="grm-grid">
                            <Field icon={FiWind}    label="Ar Primário"       value={generator.primaryAir} />
                            <Field icon={FiWind}    label="Ar Secundário"     value={generator.secundaryAir} />
                            <Field icon={FiDroplet} label="Comb. Primária"    value={generator.primaryComb} />
                            <Field icon={FiDroplet} label="Comb. Secundária"  value={generator.secundaryComb} />
                            <Field icon={FiDroplet} label="Filtro de Água"    value={generator.waterFilter} />
                            <Field icon={FiDroplet} label="Lubrificante"      value={generator.lubrificant} />
                            <Field icon={FiDroplet} label="Óleo"              value={generator.oil} />
                            <Field icon={FaFilter}  iconSize={9} label="Racor" value={generator.racor} />
                            <Field icon={FiGitMerge} label="Mangueira"        value={generator.hose} />
                            <Field icon={FiLink}    label="Correias"          value={generator.straps} />
                        </div>
                    </div>

                    <div className="grm-section">
                        <div className="grm-section-header">
                            <FiCalendar size={14} />
                            <span>Histórico de Manutenções</span>
                        </div>
                        <div className="grm-maintenance">
                            <div className="grm-maint-header">
                                <span />
                                <span className="grm-maint-col-label"><FiClock size={11} /> Última</span>
                                <span className="grm-maint-col-label"><FiCheckCircle size={11} /> Próxima</span>
                            </div>
                            <MaintRow label="Inspeção do Radiador"   last={generator.lastInspectionRadiatorDate}      next={generator.nextInspectionRadiatorDate} />
                            <MaintRow label="Bomba de Injeção"       last={generator.lastInspectionInjectionPumpDate} next={generator.nextInspectionInjectionPumpDate} />
                            <MaintRow label="Troca de Bateria"       last={generator.lastChangeBatteryDate}           next={generator.nextChangeBatteryDate} />
                            <MaintRow label="Megagem do Alternador"  last={generator.lastAlternatorMegageDate}        next={generator.nextAlternatorMegageDate} />
                            <MaintRow label="Sanitização"            last={generator.lastEquipmentSanitizationDate}   next={generator.nextEquipmentSanitizationDate} />
                            <MaintRow label="Limpeza do Tanque"      last={generator.lastTankCleaningDate}            next={generator.nextTankCleaningDate} />
                        </div>
                    </div>

                    <div className="grm-section">
                        <div className="grm-section-header">
                            <FiImage size={14} />
                            <span>Fotos</span>
                        </div>
                        <PhotoGallery
                            photos={generator.photos}
                            emptyText="Nenhuma foto cadastrada para este gerador."
                        />
                    </div>

                </div>

                <div className="grm-footer" />
            </div>
        </div>
    )
}

export default GeneratorReportModal
